// Show-on-Map Custom Tours Layer
// ===============================
//
// Wave 14cf-fix11: rendert alle Custom Tours die per
// `CustomTrail.showOnMap === true` markiert sind permanent als dünne
// sky-blue Linie auf der Karte. Mehrere Tours dürfen gleichzeitig sichtbar
// sein. Diese Anzeige ist UNABHÄNGIG vom Selection-Overlay.
//
// Wave 14cf-fix12: Plus optionale km-Marker als Symbol-Layer mit
// Text-Labels (1, 2, 3, ... bei 1km-Modus oder 10, 20, 30, ... bei
// 10km-Modus). Pro Tour separat einstellbar ueber CustomTrail.kmMarkers.

import type maplibregl from 'maplibre-gl';

export const SHOW_ON_MAP_SOURCE = 'adt-show-on-map-tours';
export const SHOW_ON_MAP_LAYER = 'adt-show-on-map-tours-line';
export const SHOW_ON_MAP_LAYER_CASING = 'adt-show-on-map-tours-casing';

export const KM_MARKER_SOURCE = 'adt-show-on-map-km-markers';
export const KM_MARKER_LAYER = 'adt-show-on-map-km-markers-symbols';

/** Idempotent: Source + Layer einrichten falls fehlend. */
export function ensureShowOnMapSourceAndLayer(map: maplibregl.Map): void {
  if (!map.getSource(SHOW_ON_MAP_SOURCE)) {
    map.addSource(SHOW_ON_MAP_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  // Casing (dunkler Outline) zuerst → liegt UNTER der Hauptlinie.
  if (!map.getLayer(SHOW_ON_MAP_LAYER_CASING)) {
    map.addLayer({
      id: SHOW_ON_MAP_LAYER_CASING,
      type: 'line',
      source: SHOW_ON_MAP_SOURCE,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        // Dunkles Casing — sorgt für Kontrast auf hellen Karten-Tiles
        // (Sand/Beach/Wiese), ohne wie die Selection-Aura zu wirken.
        'line-color': '#0c1b2e',
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10, 3,
          14, 5,
          18, 7,
        ],
        'line-opacity': 0.55,
      },
    });
  }
  if (!map.getLayer(SHOW_ON_MAP_LAYER)) {
    map.addLayer({
      id: SHOW_ON_MAP_LAYER,
      type: 'line',
      source: SHOW_ON_MAP_SOURCE,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        // Sky-blue, synced mit der Custom-Tour-Marken-Farbe (#38bdf8) —
        // wird in customTrailAsTrail als colorHex weitergereicht und in
        // der Selection-Aura als difficultyAuraColor genutzt.
        'line-color': '#38bdf8',
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10, 1.5,
          14, 2.5,
          18, 4,
        ],
        'line-opacity': 0.85,
      },
    });
  }
}

/** Setzt die Tour-Geometrien als FeatureCollection. */
export function setShowOnMapData(
  map: maplibregl.Map,
  features: GeoJSON.Feature<GeoJSON.LineString>[],
): void {
  const src = map.getSource(SHOW_ON_MAP_SOURCE) as
    | maplibregl.GeoJSONSource
    | undefined;
  if (!src) return;
  src.setData({ type: 'FeatureCollection', features });
}

/**
 * Wave 14cf-fix12: Source + Symbol-Layer fuer km-Marker entlang der
 * show-on-map Tours. Liegt UEBER dem Linien-Layer damit Labels lesbar
 * sind. Idempotent.
 */
export function ensureKmMarkerSourceAndLayer(map: maplibregl.Map): void {
  if (!map.getSource(KM_MARKER_SOURCE)) {
    map.addSource(KM_MARKER_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getLayer(KM_MARKER_LAYER)) {
    map.addLayer({
      id: KM_MARKER_LAYER,
      type: 'symbol',
      source: KM_MARKER_SOURCE,
      // Erst ab z11 anzeigen — bei weiterer Sicht waeren die Labels
      // ueberlappend und unleserlich.
      minzoom: 11,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Noto Sans Regular'],
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          11, 10,
          14, 12,
          18, 14,
        ],
        'text-anchor': 'center',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-padding': 2,
      },
      paint: {
        'text-color': '#0c1b2e',
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
        'text-halo-blur': 0.3,
      },
    });
  }
}

export function setKmMarkerData(
  map: maplibregl.Map,
  features: GeoJSON.Feature<GeoJSON.Point>[],
): void {
  const src = map.getSource(KM_MARKER_SOURCE) as
    | maplibregl.GeoJSONSource
    | undefined;
  if (!src) return;
  src.setData({ type: 'FeatureCollection', features });
}

/**
 * Berechnet Punkt-Features alle `intervalM` Meter entlang einer Polyline.
 * Liefert pro km-Marke ein Feature mit `properties.label` (z.B. "5" oder
 * "10"). Erste Marke ist intervalM (nicht 0 — der Start ist eh der Tour-
 * Anfang, kein Beschriftung noetig). Letzte Marke nur wenn sie sauber
 * unter der Gesamtlaenge liegt.
 */
export function computeKmMarkers(
  coords: Array<[number, number]>,
  intervalM: number,
): GeoJSON.Feature<GeoJSON.Point>[] {
  if (coords.length < 2 || intervalM <= 0) return [];

  // Cum. Distanzen (Haversine). Pro Index die Track-Strecke vom Start.
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const cum: number[] = new Array(coords.length);
  cum[0] = 0;
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1]!;
    const b = coords[i]!;
    const phi1 = toRad(a[1]);
    const phi2 = toRad(b[1]);
    const dphi = toRad(b[1] - a[1]);
    const dlam = toRad(b[0] - a[0]);
    const h =
      Math.sin(dphi / 2) ** 2 +
      Math.cos(phi1) * Math.cos(phi2) * Math.sin(dlam / 2) ** 2;
    const seg = 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    cum[i] = cum[i - 1]! + seg;
  }
  const total = cum[cum.length - 1]!;

  const markers: GeoJSON.Feature<GeoJSON.Point>[] = [];
  // Erste Marke bei intervalM, dann iterativ.
  for (let target = intervalM; target < total; target += intervalM) {
    // Bin search für Segment in dem `target` liegt.
    let lo = 0;
    let hi = cum.length - 1;
    while (lo < hi - 1) {
      const mid = (lo + hi) >> 1;
      if ((cum[mid] ?? 0) < target) lo = mid;
      else hi = mid;
    }
    const segStart = cum[lo] ?? 0;
    const segEnd = cum[lo + 1] ?? segStart;
    const segLen = segEnd - segStart;
    const t = segLen > 0 ? (target - segStart) / segLen : 0;
    const a = coords[lo]!;
    const b = coords[Math.min(lo + 1, coords.length - 1)]!;
    const lon = a[0] + (b[0] - a[0]) * t;
    const lat = a[1] + (b[1] - a[1]) * t;
    const km = Math.round(target / 1000);
    markers.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [lon, lat] },
      properties: { label: String(km) },
    });
  }
  return markers;
}

/** Tear-down. Defensive try-catch — Map kann zum Cleanup-Zeitpunkt
 *  bereits zerstört sein (siehe poiSymbol.ts removePoiSymbol Doku). */
export function removeShowOnMapLayer(map: maplibregl.Map): void {
  try {
    if (map.getLayer(KM_MARKER_LAYER)) map.removeLayer(KM_MARKER_LAYER);
    if (map.getSource(KM_MARKER_SOURCE)) map.removeSource(KM_MARKER_SOURCE);
    if (map.getLayer(SHOW_ON_MAP_LAYER)) map.removeLayer(SHOW_ON_MAP_LAYER);
    if (map.getLayer(SHOW_ON_MAP_LAYER_CASING))
      map.removeLayer(SHOW_ON_MAP_LAYER_CASING);
    if (map.getSource(SHOW_ON_MAP_SOURCE))
      map.removeSource(SHOW_ON_MAP_SOURCE);
  } catch {
    /* Map zerstört — egal */
  }
}
